/**
 * Task Card Component
 * Draggable card for the Kanban board
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Calendar, MessageSquare, Clock, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TaskPriorityBadge } from './TaskPriorityBadge';
import { formatDueDate, isOverdue, isToday } from '../utils/dateUtils';
import type { TaskListItem } from '../types';
import type { QuoteOption } from '../api';

interface TaskCardProps {
  task: TaskListItem;
  onClick?: () => void;
  isDragging?: boolean;
  quotes?: QuoteOption[];
}

export function TaskCard({ task, onClick, isDragging, quotes = [] }: TaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragging = isDragging || isSortableDragging;

  // Format due date with timezone awareness
  const dueDateText = formatDueDate(task.due_date);
  const dueDateIsOverdue = isOverdue(task.due_date, task.status);
  const dueDateIsToday = isToday(task.due_date);

  const dueDateInfo = dueDateText
    ? {
        text: dueDateText,
        isOverdue: dueDateIsOverdue,
        isToday: dueDateIsToday,
      }
    : null;
  const quote = task.quote_id ? quotes.find((q) => q.id === task.quote_id) : undefined;
  const quoteNumber = quote?.quote_number;

  // Get initials for avatar
  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'cursor-grab active:cursor-grabbing select-none transition-shadow',
        dragging && 'opacity-50 shadow-lg ring-2 ring-primary',
        onClick && 'cursor-pointer hover:shadow-md'
      )}
      onClick={(e) => {
        // Prevent click when dragging
        if (!dragging && onClick) {
          e.stopPropagation();
          onClick();
        }
      }}
    >
      <CardContent className="p-3 space-y-2">
        {/* Header: Task number and priority */}
        <div className="flex items-center justify-between">
          {quoteNumber ? (
            <span className="text-xs text-muted-foreground font-mono">#{quoteNumber}</span>
          ) : (
            <span className="text-xs text-muted-foreground/50 italic">No quote</span>
          )}
          <TaskPriorityBadge priority={task.priority} />
        </div>

        {/* Title */}
        <h4 className="text-sm font-medium line-clamp-2">{task.title}</h4>

        {/* Quote reference */}
        {task.quote_id && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <FileText className="h-3 w-3" />
            <span className="truncate">
              {quote?.project_name || quote?.account_name || quoteNumber || task.quote_id}
            </span>
          </div>
        )}

        {/* Footer: Meta info */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2">
            {/* Due date */}
            {dueDateInfo && (
              <div
                className={cn(
                  'flex items-center gap-1 text-xs',
                  dueDateInfo.isOverdue && 'text-destructive',
                  dueDateInfo.isToday && !dueDateInfo.isOverdue && 'text-warning'
                )}
              >
                <Calendar className="h-3 w-3" />
                <span>{dueDateInfo.text}</span>
              </div>
            )}

            {/* Time estimate */}
            {task.estimated_hours && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{task.estimated_hours}h</span>
              </div>
            )}

            {/* Comments count */}
            {task.comments_count > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <MessageSquare className="h-3 w-3" />
                <span>{task.comments_count}</span>
              </div>
            )}
          </div>

          {/* Assignee avatar */}
          {task.assigned_to_name && (
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs bg-muted">
                {getInitials(task.assigned_to_name)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>

        {/* Tags */}
        {task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {task.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 text-xs bg-muted rounded-sm text-muted-foreground"
              >
                {tag}
              </span>
            ))}
            {task.tags.length > 3 && (
              <span className="px-1.5 py-0.5 text-xs text-muted-foreground">
                +{task.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
